/**
 * Profile Service Utilities
 *
 * Helper functions and utilities for working with profile data
 */

import { type ProfileCacheEntry, type UserProfile } from './types';

// ============================================================================
// Cache Management
// ============================================================================

const CACHE_PREFIX = 'vale_profile_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Gets cached profile data if available and not expired
 */
export function getCachedProfile<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;

  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${key}`);
    if (!cached) return null;

    const entry: ProfileCacheEntry<T> = JSON.parse(cached);

    // Check if cache has expired
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(`${CACHE_PREFIX}${key}`);
      return null;
    }

    return entry.data;
  } catch (error) {
    console.error('Error reading profile cache:', error);
    return null;
  }
}

/**
 * Sets profile data in cache
 */
export function setCachedProfile<T>(
  key: string,
  data: T,
  duration = CACHE_DURATION
): void {
  if (typeof window === 'undefined') return;

  try {
    const entry: ProfileCacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + duration
    };

    localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(entry));
  } catch (error) {
    console.error('Error setting profile cache:', error);
  }
}

/**
 * Clears specific profile cache entry
 */
export function clearCachedProfile(key: string): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(`${CACHE_PREFIX}${key}`);
  } catch (error) {
    console.error('Error clearing profile cache entry:', error);
  }
}

// ============================================================================
// Profile Data Helpers
// ============================================================================

/**
 * Gets the user's display name from profile
 */
export function getUserDisplayName(profile: UserProfile): string {
  if (profile.fullName) return profile.fullName;

  const parts = [
    profile.firstName,
    profile.middleName,
    profile.lastName
  ].filter(Boolean);
  return parts.join(' ') || profile.email;
}

/**
 * Gets the user's initials from profile
 */
export function getUserInitials(profile: UserProfile): string {
  const firstName = profile.firstName || '';
  const lastName = profile.lastName || '';

  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}
